import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser'
import { useMsal } from '@azure/msal-react'
import { datadogLogs } from '@datadog/browser-logs'
import { useQueryClient } from 'react-query'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { cleanSessionStorage } from '../../pages/vehicle-configurator/util/sessionStorageUtil'
import apiClient from '../api'
import { getCurrentAppState, setAppState } from '../authAppState'
import { history } from '../historyStore'
import { isValidLocale } from '../locale'
import { getMsalUser } from '../user'
import { AppState, AuthContext, AzureLocale } from './authContext'
import { AuthFailure, AuthLoading } from './AuthFailure'
import {
  beginAuthOperation,
  clearAuthOperation,
  getOperationForLocation,
  isAuthCallback,
  safeReturnTo,
} from './authOperation'
import { getLoginRequest } from './loginRequest'

const identityKey = 'authenticatedAccount'
const localeKey = 'authenticationLocale'

const isAppState = (value: unknown): value is AppState =>
  !!value &&
  typeof value === 'object' &&
  !Array.isArray(value) &&
  Object.entries(value).every(([key, field]) =>
    key === 'driverCase'
      ? typeof field === 'boolean'
      : typeof field === 'string',
  )

const parseAppState = (state: string): AppState => {
  const parsed: unknown = JSON.parse(state)
  if (!isAppState(parsed)) {
    throw new Error('Invalid authentication application state')
  }
  return parsed
}

export const MsalContext: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { instance, accounts } = useMsal()
  const queryClient = useQueryClient()
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [failed, setFailed] = useState(false)
  const alive = useRef(true)
  const redirecting = useRef(false)
  const ignoreResetAccount = useRef(false)

  const clearSession = useCallback(() => {
    apiClient.clearAuthContext()
    queryClient.clear()
    cleanSessionStorage([])
    localStorage.removeItem('welcomeMessage')
    sessionStorage.removeItem(identityKey)
    sessionStorage.removeItem(localeKey)
  }, [queryClient])

  const prepareAccount = useCallback(
    (next: AccountInfo | null) => {
      const previous = sessionStorage.getItem(identityKey)
      if (previous && previous !== next?.homeAccountId) clearSession()
      if (next) sessionStorage.setItem(identityKey, next.homeAccountId)
      setAccount(next)
    },
    [clearSession],
  )

  useEffect(() => {
    let mounted = true
    alive.current = true
    const initializeAuth = async () => {
      const { pathname, search, hash } = window.location
      const operation = getOperationForLocation(pathname, search, hash)
      const response = await instance.handleRedirectPromise()
      if (!mounted) return
      const resetPolicy = process.env.REACT_APP_AUTH_MSAL_PW_RESET_AUTHORITY
      const resetAuthority = resetPolicy?.replace(/\/+$/, '').toLowerCase()
      const resetCompleted =
        !!response &&
        (operation?.kind === 'reset' ||
          (!!resetAuthority &&
            response.authority?.replace(/\/+$/, '').toLowerCase() ===
              resetAuthority))
      ignoreResetAccount.current = resetCompleted
      if (resetCompleted && response.account) {
        // Discard reset-policy tokens locally; do not start a second server logout.
        await instance.logoutRedirect({
          account: response.account,
          onRedirectNavigate: () => false,
        })
        if (!mounted) return
      }
      const nextAccount = resetCompleted
        ? null
        : response?.account ||
          instance.getActiveAccount() ||
          instance.getAllAccounts()[0] ||
          null
      instance.setActiveAccount(nextAccount)
      prepareAccount(nextAccount)
      if (response) {
        const appState: AppState = response.state
          ? parseAppState(response.state)
          : operation?.appState || {}
        if (appState.locale) sessionStorage.setItem(localeKey, appState.locale)
        setAppState(appState)
        clearAuthOperation()
        history.replace(
          resetCompleted ? '/auth0' : safeReturnTo(appState.returnTo),
        )
      } else {
        if (isAuthCallback(search, hash)) {
          throw new Error('Authentication callback was not handled')
        }
        if (operation?.kind === 'reset-logout') {
          setAppState(operation.appState)
          if (operation.appState.locale) {
            sessionStorage.setItem(localeKey, operation.appState.locale)
          }
        }
        clearAuthOperation()
      }
      setIsLoading(false)
    }
    initializeAuth().catch((error: unknown) => {
      if (!mounted) return
      datadogLogs.logger.error('Authentication callback failed', { error })
      setFailed(true)
      setIsLoading(false)
    })
    return () => {
      mounted = false
      alive.current = false
    }
  }, [instance, prepareAccount])

  // MSAL React updates accounts when a cached account is added or removed.
  useEffect(() => {
    if (isLoading || failed || ignoreResetAccount.current || !accounts) return
    const active = instance.getActiveAccount()
    const cached = instance.getAllAccounts()
    const next =
      cached.find((item) => item.homeAccountId === active?.homeAccountId) ||
      cached[0] ||
      null
    if (next?.homeAccountId !== account?.homeAccountId) {
      instance.setActiveAccount(next)
      prepareAccount(next)
    }
  }, [accounts, account, instance, isLoading, failed, prepareAccount])

  const getLocale = useCallback((): string => {
    const pathLocale = window.location.pathname.split('/')[1]
    return isValidLocale(pathLocale)
      ? pathLocale
      : account
      ? getMsalUser(account).userLocale
      : sessionStorage.getItem(localeKey) || 'en_IE'
  }, [account])

  const redirect = useCallback(async (action: () => Promise<void>) => {
    if (redirecting.current) return
    redirecting.current = true
    try {
      await action()
    } catch (error: unknown) {
      redirecting.current = false
      datadogLogs.logger.error('Authentication redirect failed', { error })
      if (alive.current) setFailed(true)
    }
  }, [])

  const getAccessToken = useCallback(async (): Promise<string> => {
    const active = instance.getActiveAccount()
    if (!active || active.homeAccountId !== account?.homeAccountId) {
      throw new Error('Authentication account changed')
    }
    const request = getLoginRequest(getLocale())
    try {
      const response = await instance.acquireTokenSilent({
        ...request,
        account: active,
      })
      if (
        !alive.current ||
        instance.getActiveAccount()?.homeAccountId !== active.homeAccountId
      ) {
        throw new Error(
          'Authentication account changed during token acquisition',
        )
      }
      return response.accessToken
    } catch (error: unknown) {
      if (!alive.current) throw error
      if (error instanceof InteractionRequiredAuthError) {
        if (!redirecting.current) {
          const appState = getCurrentAppState({ locale: getLocale() })
          beginAuthOperation('token', appState)
          await redirect(() =>
            instance.acquireTokenRedirect({
              ...request,
              account: active,
              state: JSON.stringify(appState),
            }),
          )
        }
      } else {
        datadogLogs.logger.error('Access token acquisition failed', { error })
      }
      throw error
    }
  }, [account, getLocale, instance, redirect])

  // Keep authenticated public calls (including the backend logout POST) covered.
  useLayoutEffect(() => {
    if (!account || isLoading || failed) return undefined
    const remove = apiClient.setAccessTokenInterceptor(getAccessToken)
    return () => {
      remove()
      queryClient.clear()
      apiClient.clearAuthContext()
    }
  }, [account, getAccessToken, isLoading, failed, queryClient])

  const value = useMemo(() => {
    const login = (state: AppState): Promise<void> => {
      if (redirecting.current) return Promise.resolve()
      const appState = getCurrentAppState({ locale: getLocale(), ...state })
      setAppState(appState)
      if (appState.locale) sessionStorage.setItem(localeKey, appState.locale)
      beginAuthOperation('login', appState)
      return redirect(() =>
        instance.loginRedirect({
          ...getLoginRequest(appState.locale),
          state: JSON.stringify(appState),
        }),
      )
    }
    const logout = () => {
      if (redirecting.current) return
      const locale = getLocale()
      clearSession()
      beginAuthOperation(
        'logout',
        { returnTo: `/${locale}/logout`, locale },
        '/',
      )
      void redirect(() =>
        instance.logoutRedirect({
          postLogoutRedirectUri: window.location.origin + '/',
        }),
      )
    }
    const passwordResetRedirect = (locale: AzureLocale): Promise<void> => {
      if (redirecting.current) return Promise.resolve()
      const appState = getCurrentAppState({
        returnTo: '/auth0',
        locale: locale.locale,
      })
      sessionStorage.setItem(localeKey, locale.locale)
      if (account) {
        const resumePath = `/password-reset?language=${encodeURIComponent(
          locale.locale,
        )}`
        clearSession()
        beginAuthOperation('reset-logout', appState, resumePath)
        return redirect(() =>
          instance.logoutRedirect({
            postLogoutRedirectUri: window.location.origin + resumePath,
          }),
        )
      }
      setAppState(appState)
      beginAuthOperation('reset', appState)
      return redirect(() =>
        instance.loginRedirect({
          ...getLoginRequest(locale.locale),
          authority: process.env.REACT_APP_AUTH_MSAL_PW_RESET_AUTHORITY,
          state: JSON.stringify(appState),
          extraQueryParameters: {
            ...getLoginRequest(locale.locale).extraQueryParameters,
            ...locale,
          },
        }),
      )
    }
    return {
      isAuthenticated: !!account,
      user: account ? getMsalUser(account) : null,
      isLoading,
      getAccessTokenSilently: getAccessToken,
      login,
      logout,
      passwordResetRedirect,
    }
  }, [
    account,
    isLoading,
    getAccessToken,
    getLocale,
    clearSession,
    redirect,
    instance,
  ])

  if (failed) return <AuthFailure />
  if (isLoading) return <AuthLoading />
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
